<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import FieldValueEditor from '../FieldValueEditor.vue'
import type { MultipartFormDataBodyContent, MultiPartBodyModel, FieldValue } from '@/types'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'
import type { ScopeDescriptor } from '@/types/bbel-types'

const props = defineProps<{
  modelValue: MultipartFormDataBodyContent
}>()

const emit = defineEmits<{
  'update:modelValue': [value: MultipartFormDataBodyContent]
}>()

// #5683 — part name/content are BBEL fields (scope provided by BuilderView; empty in read-only hosts).
const bbelScopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => bbelScopeCtx?.value ?? EMPTY_BBEL_SCOPE)

// A new part defaults to a text (expression) value — the 90% case; the per-part toggle switches a
// file part to a binary reference (ADR-034).
function newPart(): MultiPartBodyModel {
  return { name: '', value: { $type: 'expression', expression: '' } }
}

const parts = ref<MultiPartBodyModel[]>(
  props.modelValue.multipartFormContent?.length
    ? props.modelValue.multipartFormContent.map((p) => ({ ...p }))
    : [newPart()],
)

// Parts without a name are drafts — dropped from the persisted model, kept in the editor.
function emitUpdate() {
  emit('update:modelValue', {
    $type: 'multipart',
    multipartFormContent: parts.value.filter((p) => p.name.trim() !== ''),
  })
}

function addRow() {
  parts.value.push(newPart())
}

function removeRow(index: number) {
  parts.value.splice(index, 1)
  if (parts.value.length === 0) parts.value.push(newPart())
  emitUpdate()
}

function updateFileName(index: number, value: string) {
  parts.value[index].fileName = value || undefined
  emitUpdate()
}

function updateValue(index: number, value: FieldValue) {
  parts.value[index].value = value
  emitUpdate()
}
</script>

<template>
  <div class="mb-3">
    <label class="form-label">Multipart Form Data</label>
    <div v-for="(part, index) in parts" :key="index" class="border rounded p-2 mb-2">
      <div class="d-flex align-items-stretch gap-2 mb-2">
        <BbelField
          v-model="part.name"
          class="flex-grow-1"
          :scope="scope"
          single-line
          placeholder="Part name"
          @update:model-value="emitUpdate"
        />
        <button
          type="button"
          class="btn btn-outline-danger btn-sm"
          @click="removeRow(index)"
          :disabled="parts.length === 1 && !part.name"
        >
          <i class="bi bi-trash"></i>
        </button>
      </div>
      <BbelField
        :model-value="part.fileName ?? ''"
        class="mb-2"
        :scope="scope"
        single-line
        placeholder="Filename (optional)"
        @update:model-value="updateFileName(index, $event)"
      />
      <FieldValueEditor
        :model-value="part.value"
        placeholder="Part content"
        @update:model-value="updateValue(index, $event)"
      />
    </div>
    <button type="button" class="btn btn-outline-primary btn-sm" @click="addRow">
      <i class="bi bi-plus"></i> Add Part
    </button>
  </div>
</template>
